import { gql } from '@urql/core';
import { createQueryKeys } from '@lukemorales/query-key-factory';

export const webAnalyticsFeatureKeys = createQueryKeys('webAnalyticsFeature', {
  analytics: ({ view, siteId, startAt, endAt, presetDate, searchTerm, limit }) => [
    {
      view,
      siteId,
      startAt,
      endAt,
      presetDate,
      searchTerm,
      limit,
    },
  ],
});

const metricOverTimeFragment = gql`
  fragment metricOverTime on MetricOverTime {
    total
    delta
    days {
      date
      value
    }
  }
`;

const categoricalMetricFragment = gql`
  fragment categoricalMetric on CategoricalMetric {
    name
    visitors
    pageViews
    sessions
    avgSessionDuration
    bounceRate
  }
`;

const campaignMetricFragment = gql`
  fragment campaignMetric on CampaignMetric {
    name
    visitors
    pageViews
    sessions
    avgSessionDuration
    bounceRate
  }
`;

const chartFields = `
  # Values for the chart
  uniqueVisitors {
    ...metricOverTime
  }
  pageViews {
    ...metricOverTime
  }
  avgSessionDuration {
    ...metricOverTime
  }
  sessions {
    ...metricOverTime
  }
  bounceRate {
    ...metricOverTime
  }
`;

const visitorsField = `
  visitors {
    new
    returning
    total
  }
`;

const deviceTypeField = `
  deviceType {
    ...categoricalMetric
  }
`;

const countriesField = `
  countries(limit: $limit) {
    ...categoricalMetric
  }
`;

const languagesField = `
  languages(limit: $limit) {
    ...categoricalMetric
  }
`;

const osField = `
  os(limit: $limit) {
    ...categoricalMetric
  }
`;

const browsersField = `
  browsers(limit: $limit) {
    ...categoricalMetric
  }
`;

const channelsField = `
  channels(limit: $limit) {
    ...categoricalMetric
  }
`;

const referrersField = `
  referrers(limit: $limit) {
    ...categoricalMetric
  }
`;

const pagesField = `
  pages(limit: $limit) {
    name
    pageViews
    avgSessionDuration
  }
`;

const entryPagesField = `
  entryPages(limit: $limit) {
    name
    visitors
    pageViews
    sessions
    avgTimeOnPage
    avgSessionDuration
    bounceRate
  }
`;

const utmCampaignsField = `
  utmCampaigns(limit: $limit) {
    ...campaignMetric
  }
`;

const utmSourcesField = `
  utmSources(limit: $limit) {
    ...campaignMetric
  }
`;

const utmMediumsField = `
  utmMediums(limit: $limit) {
    ...campaignMetric
  }
`;

const utmTermsField = `
  utmTerms(limit: $limit) {
    ...campaignMetric
  }
`;

const utmContentsField = `
  utmContents(limit: $limit) {
    ...campaignMetric
  }
`;

const categoryFields = ['devices', 'countries', 'languages', 'os', 'browsers', 'channels', 'referrers'];

export const webAnalyticsQuery = ({ siteId, startAt, endAt, presetDate, searchTerm, view = 'ALL', limit }) => ({
  ...webAnalyticsFeatureKeys.analytics({ view, siteId, startAt, endAt, presetDate, searchTerm, limit }),
  meta: {
    query: gql`
      query WebAnalytics($siteId: Int!, $startAt: Int, $endAt: Int, $presetDate: String, $searchTerm: String, $limit: Int) {
        analytics(siteId: $siteId, startAt: $startAt, endAt: $endAt, presetDate: $presetDate, searchTerm: $searchTerm) {
          ${view === 'ALL' ? chartFields : ''}
          # Values for the everything else
          ${view === 'ALL' ? visitorsField : ''}
          ${view === 'ALL' ? deviceTypeField : ''}
          ${view === 'ALL' || view === 'countries' ? countriesField : ''}
          ${view === 'ALL' || view === 'languages' ? languagesField : ''}
          ${view === 'ALL' || view === 'os' ? osField : ''}
          ${view === 'ALL' || view === 'browsers' ? browsersField : ''}
          ${view === 'ALL' || view === 'channels' ? channelsField : ''}
          ${view === 'ALL' || view === 'referrers' ? referrersField : ''}
          ${view === 'ALL' || view === 'pages' ? pagesField : ''}
          ${view === 'ALL' || view === 'entryPages' ? entryPagesField : ''}
          ${view === 'ALL' || view === 'utmCampaigns' ? utmCampaignsField : ''}
          ${view === 'ALL' || view === 'utmSources' ? utmSourcesField : ''}
          ${view === 'ALL' || view === 'utmMediums' ? utmMediumsField : ''}
          ${view === 'ALL' || view === 'utmTerms' ? utmTermsField : ''}
          ${view === 'ALL' || view === 'utmContents' ? utmContentsField : ''}
        }
      }

      ${view === 'ALL' ? metricOverTimeFragment : ''}
      ${view === 'ALL' || categoryFields.includes(view) ? categoricalMetricFragment : ''}
      ${view === 'ALL' || view.includes('utm') ? campaignMetricFragment : ''}
    `,
  },
});
